import { useState, useEffect, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SessionContext } from "../context/SessionContext";
import { ProfileContext } from "../context/ProfileContext";
import { v4 as uuidv4 } from "uuid";

import CreatableSelect from "react-select/creatable";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Skeleton from "react-loading-skeleton";
import Dropdown from "react-bootstrap/Dropdown";
import Nav from "react-bootstrap/Nav";
import toast from "react-hot-toast";
import PersonnelDetails from "../components/PersonnelDetails";
import ToastDisplay from "../components/ToastDisplay";
import InfoLogs from "../components/InfoLogs";
import FileUpload from "../components/FileUpload";

const PersonnelInfo = () => {
	let { id } = useParams();
	const navigate = useNavigate();
	const photoRef = useRef(null);
	const [session] = useContext(SessionContext);
	const [profile] = useContext(ProfileContext);
	const [loading, setLoading] = useState(true);
	const [personnel, setPersonnel] = useState(null);
	const [editModal, setEditModal] = useState(false);
	const [deleteModal, setDeleteModal] = useState(false);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [actionLoading, setActionLoading] = useState(false);
	const [departmentLoading, setDepartmentLoading] = useState(false);
	const [departments, setDepartments] = useState([]);

	const [name, setName] = useState("");
	const [personnelNo, setPersonnelNo] = useState("");
	const [rfidNo, setRfidNo] = useState("");
	const [department, setDepartment] = useState({});
	const [msisdn, setMsisdn] = useState("");
	const [photo, setPhoto] = useState(null);
	const [photoPath, setPhotoPath] = useState("");

	const [errors, setErrors] = useState([]);
	const [tab, setTab] = useState("details");

	const requiredFields = [
		{ key: "name", value: name },
		{ key: "personnelNo", value: personnelNo },
		{ key: "rfidNo", value: rfidNo },
	];

	const getData = async () => {
		setLoading(true);
		const requestOptions = {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/personnel/p/${id}`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content="Something went wrong." />);
		} else {
			setDepartments(
				data.departments.map((dept) => ({
					value: dept.name,
					label: dept.name,
				}))
			);
			setPersonnel(data.personnel);
			setName(data.personnel.name);
			setPersonnelNo(data.personnel.personnel_no);
			setMsisdn(data.personnel.msisdn);
			setRfidNo(data.personnel.id_no);
			setPhotoPath(data.personnel.photo);
			setDepartment({
				value: data.personnel.department,
				label: data.personnel.department,
			});
		}
		setLoading(false);
	};

	const editPersonnel = async () => {
		setActionLoading(true);
		const requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
			body: JSON.stringify({
				name: name,
				personnel_no: personnelNo,
				id_no: rfidNo,
				department: department?.value || "",
				msisdn: msisdn,
				photo: photoPath,
			}),
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/personnel/p/${id}`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content={data.msg} />);
		} else {
			toast(<ToastDisplay status="success" content={data.msg} />);
			getData();
			closeEditModal();
		}
		setActionLoading(false);
	};

	const deletePersonnel = async () => {
		setDeleteLoading(true);
		const requestOptions = {
			method: "DELETE",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/personnel/p/${id}`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content={data.msg} />);
		} else {
			toast(<ToastDisplay status="success" content={data.msg} />);
			navigate("/personnel");
		}
		setDeleteModal(false);
		setDeleteLoading(false);
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		setActionLoading(true);
		var newErrors = [];

		requiredFields.map((field) => {
			if (!field.value || field.value === "" || field.value.length === 0) {
				newErrors.push(field.key);
			}
		});

		if (newErrors.length !== 0) {
			toast(
				<ToastDisplay
					status="error"
					content="Please fill out all required fields."
				/>
			);
			setActionLoading(false);
			setErrors(newErrors);
			return;
		}
		editPersonnel();
	};

	const handlePhoto = (e) => {
		const file = e.target.files[0];
		const newUid = uuidv4();
		const uuid_name = `${newUid}.${file.name.split(".").pop()}`;

		const forUpload = {
			uuid: uuid_name,
			file: file,
		};
		setPhoto(forUpload);
		setPhotoPath(
			`${process.env.REACT_APP_STORAGE_URL}/pisara/personnel/${uuid_name}`
		);
	};

	const closeEditModal = () => {
		setEditModal(false);
		setName(personnel.name);
		setPersonnelNo(personnel.personnel_no);
		setMsisdn(personnel.msisdn);
		setRfidNo(personnel.id_no);
		setPhotoPath(personnel.photo || "");
		setPhoto(null);
		setDepartment({
			value: personnel.department,
			label: personnel.department,
		});

		setErrors([]);
	};

	const createDepartment = async (inputValue) => {
		setDepartmentLoading(true);
		const requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				"X-Auth-Token": session,
			},
			body: JSON.stringify({
				departments: [inputValue],
			}),
		};

		const response = await fetch(
			`${process.env.REACT_APP_API_URI}/personnel/departments/`,
			requestOptions
		);
		const data = await response.json();

		if (!response.ok) {
			toast(<ToastDisplay status="error" content={data.msg} />);
		} else {
			setDepartments([
				...departments,
				{ value: inputValue, label: inputValue },
			]);
			setDepartment({ value: inputValue, label: inputValue });
		}
		setDepartmentLoading(false);
	};

	useEffect(() => {
		if (profile) {
			if (!profile.role.personnel_view) {
				navigate("/");
			} else {
				if (id) {
					getData();
				}
			}
		}
	}, [id, profile]);

	return (
		<>
			<Container className="gx-5">
				<div className="info-page-header mb-10">
					<a
						href="#"
						className="back-btn flex-row-left fit mb-20"
						onClick={(e) => {
							e.preventDefault();
							navigate(-1);
						}}
					>
						<i className="ri-arrow-left-line opacity-7" />
						<span className="text-sm text-regular-inter upper opacity-7">
							Personnel
						</span>
					</a>
					<Row className="flex-row-left mb-30">
						<Col className="flex-row-left">
							<div
								className="contact-icon flex-column rounded-8 bg-cover border"
								style={{ background: `url(${personnel?.photo})` }}
							>
								{!personnel?.photo && (
									<span className="text-bold-gotham text-md">
										{personnel?.name[0]}
									</span>
								)}
							</div>
							<h1 className="text-lg mb-0 text-medium-gotham">
								{loading ? (
									<Skeleton
										width={250}
										height={25}
										baseColor="#dadee7"
										highlightColor="#e9ecf2"
									/>
								) : (
									personnel?.name || "--"
								)}
							</h1>
						</Col>
						<Col className="flex-row-right">
							{profile?.role?.personnel_edit ||
							profile?.role?.personnel_delete ? (
								<Dropdown>
									<Dropdown.Toggle
										variant="light"
										className="info-control-btn text-lg rounded-4 flex-column no-arrow"
										disabled={loading}
									>
										<i className="ri-more-2-fill me-0" />
									</Dropdown.Toggle>
									<Dropdown.Menu className="text-sm dropdown-default">
										{profile?.role?.personnel_edit && (
											<>
												<Dropdown.Item
													href="#"
													onClick={(e) => {
														e.preventDefault();
														setEditModal(true);
													}}
												>
													Edit
												</Dropdown.Item>
											</>
										)}
										{profile?.role?.personnel_delete && (
											<Dropdown.Item
												href="#"
												className="text-danger"
												onClick={() => setDeleteModal(true)}
											>
												Delete
											</Dropdown.Item>
										)}
									</Dropdown.Menu>
								</Dropdown>
							) : (
								""
							)}
						</Col>
					</Row>
				</div>
				<Nav variant="tabs" defaultActiveKey={tab} className="mb-20">
					<Nav.Item>
						<Nav.Link
							eventKey="details"
							className="info-tab-item"
							onClick={() => setTab("details")}
						>
							Details
						</Nav.Link>
					</Nav.Item>
					<Nav.Item>
						<Nav.Link
							eventKey="logs"
							className="info-tab-item"
							onClick={() => setTab("logs")}
						>
							Logs
						</Nav.Link>
					</Nav.Item>
				</Nav>
				{tab === "details" && (
					<PersonnelDetails loading={loading} personnel={personnel} />
				)}
				{tab === "logs" && <InfoLogs loading={loading} data={personnel} />}
			</Container>
			<Modal
				show={editModal}
				className="info-modal"
				size="lg"
				centered
				onHide={closeEditModal}
			>
				<Form onSubmit={handleSubmit}>
					<Modal.Header>
						<Modal.Title className="text-lg">Edit Personnel</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<div className="mb-30 full flex-column">
							<div className="file-upload-container">
								{photoPath && !photo && (
									<div
										className="file-upload bg-cover rounded-4 border"
										style={{
											background: `url(${photoPath})`,
										}}
									>
										<div className="file-delete-overlay flex-column">
											<i
												className="ri-delete-bin-line text-white m-0 text-lg"
												onClick={() => setPhotoPath("")}
											/>
										</div>
									</div>
								)}
								{photo && (
									<FileUpload
										file={photo}
										files={photo}
										setFiles={setPhoto}
										filePaths={photoPath}
										setFilePaths={setPhotoPath}
										directory="pisara/personnel"
									/>
								)}
								{!photoPath && (
									<div
										className="file-btn border rounded-8"
										onClick={() => photoRef.current.click()}
									>
										<div className="overlay" />
										<img
											src="/default-user.png"
											alt=""
											className="default-user-photo"
										/>
									</div>
								)}
								<input
									ref={photoRef}
									type="file"
									className="hidden"
									accept="image/*"
									onChange={handlePhoto}
								/>
							</div>
						</div>
						<div className="mb-10">
							<h2 className="text-lg text-medium-gotham mb-0">
								Personnel Information
							</h2>
						</div>
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">Name</Form.Label>
								<Form.Control
									autoFocus
									className={`text-sm form-input ${
										errors.includes("name") && "border-danger"
									}`}
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
							</Col>
						</Row>
						<Row className="mb-30">
							<Col>
								<Form.Label className="text-sm">Personnel No.</Form.Label>
								<Form.Control
									className={`text-sm form-input ${
										errors.includes("personnelNo") && "border-danger"
									}`}
									value={personnelNo}
									onChange={(e) => setPersonnelNo(e.target.value)}
								/>
							</Col>
							<Col>
								<Form.Label className="text-sm">Department</Form.Label>
								<CreatableSelect
									isClearable
									isDisabled={departmentLoading}
									isLoading={departmentLoading}
									onCreateOption={createDepartment}
									value={department}
									onChange={setDepartment}
									options={departments}
									menuShouldScrollIntoView={false}
									maxMenuHeight={200}
									placeholder=""
									styles={{
										control: (baseStyles, state) => ({
											...baseStyles,
											height: "38px",
											outline: "none",
											borderRadius: 4,
											fontSize: "0.875rem",
											borderColor: state.isFocused
												? "#86b7fe !important"
												: "#dee2e6 !important",
											boxShadow: state.isFocused
												? "0 0 0 .25rem rgba(13, 110, 253, .25)"
												: "none",
										}),
										option: (baseStyles, state) => ({
											...baseStyles,
											fontSize: ".875rem",
											color: "#050911",
											backgroundColor: state.isFocused
												? "#c8deff !important"
												: "#fff",
										}),
									}}
								/>
							</Col>
						</Row>
						<Row className="mb-50">
							<Col>
								<Form.Label className="text-sm">Mobile No.</Form.Label>
								<Form.Control
									className={`text-sm form-input ${
										errors.includes("msisdn") && "border-danger"
									}`}
									value={msisdn}
									onChange={(e) => setMsisdn(e.target.value)}
								/>
							</Col>
							<Col>
								<Form.Label className="text-sm">RFID No.</Form.Label>
								<Form.Control
									className={`text-sm form-input ${
										errors.includes("rfidNo") && "border-danger"
									}`}
									value={rfidNo}
									onChange={(e) => setRfidNo(e.target.value)}
								/>
							</Col>
						</Row>
					</Modal.Body>
					<Modal.Footer>
						<Button
							variant="outline-secondary"
							className="btn-sm action-btn text-sm me-2"
							onClick={closeEditModal}
						>
							Close
						</Button>
						<Button
							type="submit"
							variant="primary"
							className="btn-sm action-btn text-sm btn-default"
							disabled={actionLoading}
						>
							{actionLoading ? (
								<Spinner className="btn-loader text-white" />
							) : (
								"Save Changes"
							)}
						</Button>
					</Modal.Footer>
				</Form>
			</Modal>
			<Modal show={deleteModal} onHide={() => setDeleteModal(false)}>
				<Modal.Body>
					<h2 className="text-lg text-medium-inter mb-15">Delete Personnel</h2>
					<p className="text-sm opacity-7">
						Are you sure you want to delete this personnel? This action cannot
						be undone.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="light"
						className="modal-btn btn-sm dismiss-btn text-medium-inter text-sm"
						onClick={() => setDeleteModal(false)}
					>
						Cancel
					</Button>
					<Button
						variant="danger"
						className="action-btn btn-sm text-medium-inter text-sm flex-column"
						disabled={deleteLoading}
						onClick={deletePersonnel}
					>
						{deleteLoading ? (
							<Spinner className="btn-loader text-white" />
						) : (
							"Delete"
						)}
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default PersonnelInfo;
